import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import ModalImage from 'react-modal-image';
import { isAllowedMediaUrl } from './mediaUrl';

const PdfViewerLazy = lazy(() => import('./PdfViewerLazy'));

/** Soft 4:3 reserve so the bubble height is stable before pixels arrive */
const IMAGE_ASPECT = '4 / 3';
/** Soft landscape reserve until video metadata arrives */
const VIDEO_ASPECT_FALLBACK = '16 / 9';

function preferTheaterFullscreen() {
  if (typeof window === 'undefined') return true;

  const coarse = window.matchMedia('(hover: none), (pointer: coarse)').matches;
  const narrow = window.matchMedia('(max-width: 900px)').matches;
  const isIOS =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // Mobile web + iOS: chat shell / Virtuoso often break native FS layout
  return coarse || narrow || isIOS || !document.fullscreenEnabled;
}

function getViewportOrientation() {
  if (typeof window === 'undefined') return 'landscape';
  return window.matchMedia('(orientation: landscape)').matches
    ? 'landscape'
    : 'portrait';
}

function ChatImage({ fileUrl, onLoad }) {
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setReady(false);
    setFailed(false);

    const probe = new Image();
    let cancelled = false;

    const finish = () => {
      if (cancelled) return;
      setReady(true);
      onLoad?.();
    };

    probe.onload = finish;
    probe.onerror = () => {
      if (cancelled) return;
      setFailed(true);
      onLoad?.();
    };
    probe.src = fileUrl;

    if (probe.complete && probe.naturalWidth > 0) {
      finish();
    }

    return () => {
      cancelled = true;
      probe.onload = null;
      probe.onerror = null;
    };
  }, [fileUrl, onLoad]);

  return (
    <span
      className={`message-image-frame${ready ? ' is-ready' : ''}${failed ? ' is-failed' : ''}`}
      // 4:3 reserve only while the skeleton shows; once loaded the image keeps
      // its natural aspect (WhatsApp-style), capped by CSS max-height.
      style={ready && !failed ? undefined : { aspectRatio: IMAGE_ASPECT }}
    >
      {!ready && !failed && (
        <span className="message-image-skeleton" aria-hidden="true" />
      )}
      {failed ? (
        <span className="message-image-fallback">Image unavailable</span>
      ) : (
        <ModalImage
          small={fileUrl}
          large={fileUrl}
          alt="Media"
          className="message-image"
        />
      )}
    </span>
  );
}

function useFocusTrap(containerRef, active, onEscape) {
  useEffect(() => {
    if (!active) return undefined;

    const root = containerRef.current;
    if (!root) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusableSelector =
      'button:not([disabled]), [href], input, select, textarea, video, [tabindex]:not([tabindex="-1"])';

    const getFocusable = () =>
      Array.from(root.querySelectorAll(focusableSelector)).filter(
        (el) => !el.hasAttribute('disabled') && el.offsetParent !== null
      );

    const focusable = getFocusable();
    const first = focusable[0];
    if (first) first.focus();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onEscape?.();
        return;
      }

      if (event.key !== 'Tab') return;

      const items = getFocusable();
      if (!items.length) return;

      const firstItem = items[0];
      const lastItem = items[items.length - 1];

      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault();
        firstItem.focus();
      }
    };

    root.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      root.removeEventListener('keydown', onKeyDown);
    };
  }, [active, containerRef, onEscape]);
}

function VideoTheaterShell({
  open,
  setStageNode,
  onClose,
  closeButtonRef,
  orientationHint,
  onDismissHint,
  titleId,
  statusId,
}) {
  const dialogRef = useRef(null);
  useFocusTrap(dialogRef, open, onClose);

  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      ref={dialogRef}
      className="video-theater"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={orientationHint ? statusId : undefined}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <h2 id={titleId} className="video-theater-title">
        Video player
      </h2>
      <button
        ref={closeButtonRef}
        type="button"
        className="video-theater-close"
        onClick={onClose}
        aria-label="Close video"
      >
        Close
      </button>

      {orientationHint && (
        <p
          id={statusId}
          className="video-theater-hint"
          role="status"
          aria-live="polite"
          onClick={onDismissHint}
        >
          {orientationHint}
        </p>
      )}

      <div
        ref={setStageNode}
        className="video-theater-stage"
        onClick={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
      />
    </div>,
    document.body
  );
}

function ChatVideo({ fileUrl, fileExtension, onLoad }) {
  const titleId = useId();
  const statusId = useId();
  const frameRef = useRef(null);
  const inlineSlotRef = useRef(null);
  const theaterSlotRef = useRef(null);
  const videoRef = useRef(null);
  const expandButtonRef = useRef(null);
  const closeButtonRef = useRef(null);
  const resumeFocusRef = useRef(null);
  const onLoadRef = useRef(onLoad);
  const paintedRef = useRef(false);
  const readyFallbackRef = useRef(null);

  const [aspectRatio, setAspectRatio] = useState(VIDEO_ASPECT_FALLBACK);
  const [orientation, setOrientation] = useState('landscape');
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [theaterOpen, setTheaterOpen] = useState(false);
  const [activeSlot, setActiveSlot] = useState(null);
  const [posterUrl, setPosterUrl] = useState('');
  const [orientationHint, setOrientationHint] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    onLoadRef.current = onLoad;
  }, [onLoad]);

  const setInlineSlot = useCallback(
    (node) => {
      inlineSlotRef.current = node;
      if (!theaterOpen && node) {
        setActiveSlot(node);
      }
    },
    [theaterOpen]
  );

  const setTheaterSlot = useCallback(
    (node) => {
      theaterSlotRef.current = node;
      if (theaterOpen && node) {
        setActiveSlot(node);
      } else if (theaterOpen && !node && inlineSlotRef.current) {
        // Theater unmounting — park the player back in the bubble immediately
        setActiveSlot(inlineSlotRef.current);
      }
    },
    [theaterOpen]
  );

  // Lazy-load: wait until the bubble is near the viewport
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || shouldLoad) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setShouldLoad(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '160px 0px' }
    );

    observer.observe(frame);
    return () => observer.disconnect();
  }, [shouldLoad, fileUrl, reloadToken]);

  // Fallback if callback refs raced with theaterOpen
  useLayoutEffect(() => {
    if (theaterOpen && theaterSlotRef.current) {
      setActiveSlot(theaterSlotRef.current);
    } else if (!theaterOpen && inlineSlotRef.current) {
      setActiveSlot(inlineSlotRef.current);
    }
  }, [theaterOpen, shouldLoad, failed, reloadToken]);

  const capturePoster = useCallback((video) => {
    if (!video.videoWidth || !video.videoHeight) return;

    try {
      const maxW = 480;
      const scale = Math.min(1, maxW / video.videoWidth);
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
      canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.72);
      if (dataUrl && dataUrl.startsWith('data:image')) {
        setPosterUrl(dataUrl);
      }
    } catch {
      // Cross-origin videos without CORS taint the canvas — first frame still shows on <video>
    }
  }, []);

  const markReady = useCallback(
    (video) => {
      if (paintedRef.current) return;
      paintedRef.current = true;
      if (readyFallbackRef.current) {
        clearTimeout(readyFallbackRef.current);
        readyFallbackRef.current = null;
      }

      const { videoWidth, videoHeight } = video;
      if (videoWidth > 0 && videoHeight > 0) {
        setAspectRatio(`${videoWidth} / ${videoHeight}`);
        setOrientation(videoWidth >= videoHeight ? 'landscape' : 'portrait');
      }

      capturePoster(video);
      setReady(true);
      setFailed(false);
      onLoadRef.current?.();
    },
    [capturePoster]
  );

  const handleLoadedMetadata = useCallback(
    (event) => {
      const video = event.currentTarget;
      const { videoWidth, videoHeight } = video;

      if (videoWidth > 0 && videoHeight > 0) {
        setAspectRatio(`${videoWidth} / ${videoHeight}`);
        setOrientation(videoWidth >= videoHeight ? 'landscape' : 'portrait');
      }

      // Nudge decode so a first frame paints (acts as poster when canvas is blocked)
      const paint = () => markReady(video);

      if (video.readyState >= 2) {
        if (video.currentTime < 0.01) {
          const onSeeked = () => {
            video.removeEventListener('seeked', onSeeked);
            paint();
          };
          video.addEventListener('seeked', onSeeked);
          try {
            video.currentTime = 0.001;
          } catch {
            paint();
          }
        } else {
          paint();
        }
      } else if (!readyFallbackRef.current) {
        /*
          iOS / iPadOS Safari stops at metadata: with preload="metadata" it
          never fetches a frame until the user presses play, so `loadeddata`
          does not fire and the player sat on its loading state for good — with
          the play control hidden behind it. The metadata IS enough to show the
          player, so if no frame has arrived shortly, show it anyway.
        */
        readyFallbackRef.current = setTimeout(() => {
          readyFallbackRef.current = null;
          paint();
        }, 1500);
      }
    },
    [markReady]
  );

  useEffect(
    () => () => {
      if (readyFallbackRef.current) clearTimeout(readyFallbackRef.current);
    },
    []
  );

  const handleLoadedData = useCallback(
    (event) => {
      markReady(event.currentTarget);
    },
    [markReady]
  );

  const handleError = useCallback(() => {
    setFailed(true);
    setReady(false);
    onLoadRef.current?.();
  }, []);

  const handleRetry = useCallback(() => {
    paintedRef.current = false;
    setFailed(false);
    setReady(false);
    setPosterUrl('');
    setShouldLoad(true);
    setReloadToken((token) => token + 1);
  }, []);

  const closeTheater = useCallback(() => {
    setTheaterOpen(false);
    setOrientationHint('');

    requestAnimationFrame(() => {
      const resume = resumeFocusRef.current;
      if (resume && typeof resume.focus === 'function') {
        resume.focus();
      } else {
        expandButtonRef.current?.focus();
      }
    });
  }, []);

  const openTheater = useCallback(() => {
    resumeFocusRef.current = document.activeElement;
    setTheaterOpen(true);
  }, []);

  const requestNativeFullscreen = useCallback(async () => {
    const video = videoRef.current;
    const frame = frameRef.current;
    if (!video) return false;

    try {
      if (typeof video.webkitEnterFullscreen === 'function') {
        video.webkitEnterFullscreen();
        return true;
      }

      const target = frame || video;
      if (target.requestFullscreen) {
        await target.requestFullscreen();
        return true;
      }
      if (target.webkitRequestFullscreen) {
        await target.webkitRequestFullscreen();
        return true;
      }
    } catch {
      return false;
    }

    return false;
  }, []);

  const handleExpand = useCallback(async () => {
    if (preferTheaterFullscreen()) {
      openTheater();
      return;
    }

    const usedNative = await requestNativeFullscreen();
    if (!usedNative) {
      openTheater();
    }
  }, [openTheater, requestNativeFullscreen]);

  // Orientation coaching while theater is open
  useEffect(() => {
    if (!theaterOpen) {
      setOrientationHint('');
      return undefined;
    }

    const updateHint = () => {
      const viewport = getViewportOrientation();
      if (orientation === 'landscape' && viewport === 'portrait') {
        setOrientationHint('Rotate your phone for a wider view');
      } else if (orientation === 'portrait' && viewport === 'landscape') {
        setOrientationHint('Rotate to portrait for a taller view');
      } else {
        setOrientationHint('');
      }
    };

    updateHint();

    const mq = window.matchMedia('(orientation: landscape)');
    const onChange = () => updateHint();
    mq.addEventListener?.('change', onChange);
    window.addEventListener('orientationchange', onChange);

    const dismissTimer = window.setTimeout(() => {
      setOrientationHint('');
    }, 4500);

    return () => {
      window.clearTimeout(dismissTimer);
      mq.removeEventListener?.('change', onChange);
      window.removeEventListener('orientationchange', onChange);
    };
  }, [theaterOpen, orientation]);

  // Double-click expands; single taps stay with native controls
  useEffect(() => {
    const video = videoRef.current;
    if (!video || failed) return undefined;

    const onDblClick = (event) => {
      event.preventDefault();
      handleExpand();
    };

    video.addEventListener('dblclick', onDblClick);
    return () => {
      video.removeEventListener('dblclick', onDblClick);
    };
  }, [handleExpand, failed, shouldLoad, reloadToken, activeSlot]);

  const videoPlayer =
    shouldLoad && !failed && activeSlot
      ? ReactDOM.createPortal(
          <video
            key={`video-${reloadToken}`}
            ref={videoRef}
            controls
            playsInline
            preload={ready ? 'auto' : 'metadata'}
            poster={posterUrl || undefined}
            className={`message-video${theaterOpen ? ' is-theater' : ''}`}
            onLoadedMetadata={handleLoadedMetadata}
            onLoadedData={handleLoadedData}
            onCanPlay={handleLoadedData}
            onError={handleError}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
          >
            <source src={fileUrl} type={`video/${fileExtension}`} />
          </video>,
          activeSlot
        )
      : null;

  return (
    <>
      <div
        ref={frameRef}
        className={`message-video-frame is-${orientation}${ready ? ' is-ready' : ''}${failed ? ' is-failed' : ''}${theaterOpen ? ' is-theater-open' : ''}`}
        style={{ aspectRatio }}
      >
        {!ready && !failed && (
          <span className="message-video-skeleton" aria-hidden="true" />
        )}

        {failed ? (
          <div className="message-video-fallback" role="alert">
            <span>Video unavailable</span>
            <button type="button" className="message-video-retry" onClick={handleRetry}>
              Retry
            </button>
          </div>
        ) : (
          <>
            <div
              ref={setInlineSlot}
              className="message-video-slot"
              hidden={theaterOpen}
            />

            {theaterOpen && posterUrl && (
              <img
                src={posterUrl}
                alt=""
                className="message-video-bubble-poster"
                aria-hidden="true"
              />
            )}

            <div className="message-video-chrome" aria-hidden={theaterOpen}>
              <button
                ref={expandButtonRef}
                type="button"
                className="message-video-expand"
                onClick={handleExpand}
                aria-label="Watch fullscreen. Double-click the video to expand."
                title="Fullscreen"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path
                    fill="currentColor"
                    d="M7 14H5v5h5v-2H7v-3zm0-4h2V7h3V5H5v5h2zm10 7h-3v2h5v-5h-2v3zm-3-9V5h5v5h-2V7h-3z"
                  />
                </svg>
                <span className="message-video-expand-label">Fullscreen</span>
              </button>
            </div>

            <span className="sr-only" role="status" aria-live="polite">
              {isPlaying ? 'Video playing' : ready ? 'Video paused' : 'Loading video'}
            </span>
          </>
        )}
      </div>

      <VideoTheaterShell
        open={theaterOpen}
        setStageNode={setTheaterSlot}
        onClose={closeTheater}
        closeButtonRef={closeButtonRef}
        orientationHint={orientationHint}
        onDismissHint={() => setOrientationHint('')}
        titleId={titleId}
        statusId={statusId}
      />
      {videoPlayer}
    </>
  );
}

function MessageMedia({ fileUrl, onLoad }) {
  const [shown, setShown] = useState(false);

  if (!fileUrl || !isAllowedMediaUrl(fileUrl)) return null;

  const fileExtension = fileUrl.split('.').pop().toLowerCase().split('?')[0];
  const fileName = fileUrl.split('/').pop();

  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension)) {
    return <ChatImage fileUrl={fileUrl} onLoad={onLoad} />;
  }

  if (['mp4', 'webm'].includes(fileExtension)) {
    return (
      <ChatVideo
        fileUrl={fileUrl}
        fileExtension={fileExtension}
        onLoad={onLoad}
      />
    );
  }

  if (['mp3', 'wav'].includes(fileExtension)) {
    return (
      <audio controls className="message-audio" onLoadedMetadata={() => onLoad?.()}>
        <source src={fileUrl} type={`audio/${fileExtension}`} />
      </audio>
    );
  }

  if (fileExtension === 'pdf') {
    return (
      <div className="pdf-message">
        <img src="/pdf.png" alt="PDF Icon" className="pdf-icon" />
        <span
          style={{
            color: '#00449e',
            textDecoration: 'underline',
            cursor: 'pointer',
            marginLeft: '10px',
          }}
          onClick={() => setShown(true)}
        >
          {fileName}
        </span>
        {shown && (
          <Suspense fallback={null}>
            <PdfViewerLazy fileUrl={fileUrl} onClose={() => setShown(false)} />
          </Suspense>
        )}
      </div>
    );
  }

  return null;
}

export default MessageMedia;
